import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import * as z from 'zod';
import { useStartWriteSpan } from './useStartWriteSpan';
import { WRITE_SPAN_NAMES } from './writeSpanNames';

const variablesSchema = z.object({
  body: z
    .object({ status: z.string().optional(), type: z.string().optional() })
    .optional(),
});

/**
 * Times the writes in `WRITE_SPAN_NAMES` from the call until the result is on
 * screen. Mount it once; it listens to every `useApiMutation`.
 */
export function useWriteSpans() {
  const queryClient = useQueryClient();
  const startWriteSpan = useStartWriteSpan();
  const settlers = useRef(new Map<number, (failed: boolean) => void>());

  useEffect(() => {
    return queryClient.getMutationCache().subscribe((event) => {
      if (event.type !== 'updated') return;

      const { mutation, action } = event;

      if (action.type === 'pending') {
        const [method, path] = mutation.options.mutationKey ?? [];
        const entry = WRITE_SPAN_NAMES[`${method} ${path}`];
        if (!entry) return;

        // A write without a typed body, such as one without variables, keeps
        // the endpoint's default action name.
        const body = variablesSchema.safeParse(action.variables).data?.body;
        settlers.current.set(
          mutation.mutationId,
          startWriteSpan(
            typeof entry === 'function' ? entry(body ?? {}) : entry
          )
        );
        return;
      }

      if (action.type !== 'success' && action.type !== 'error') return;

      settlers.current.get(mutation.mutationId)?.(action.type === 'error');
      settlers.current.delete(mutation.mutationId);
    });
  }, [queryClient, startWriteSpan]);
}
